import { useEffect, useState } from 'react'
import { Users } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import { guildIconUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import type { Invite } from '@/types/api'
import styles from './InvitePage.module.css'

/** Страница /invite/<code>: превью сервера и кнопка «Принять приглашение». */
export function InvitePage({ code }: { code: string }) {
  const setPendingInvite = useAppStore((s) => s.setPendingInvite)
  const openGuild = useAppStore((s) => s.openGuild)
  const joinByCode = useGuildStore((s) => s.joinByCode)
  const alreadyIn = useGuildStore((s) => s.guilds)
  const [invite, setInvite] = useState<Invite | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    guildApi
      .invite(code)
      .then((inv) => { if (!cancelled) setInvite(inv) })
      .catch((err: unknown) => { if (!cancelled) setError(describeError(err).message) })
    return () => { cancelled = true }
  }, [code])

  const finish = () => {
    setPendingInvite(null)
    history.replaceState({}, '', '/')
  }
  const accept = async () => {
    if (!invite) return
    setBusy(true)
    try {
      const inv = await joinByCode(invite.code)
      openGuild(inv.guild.id, inv.channel.id)
      finish()
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }
  const member = invite ? Boolean(alreadyIn[invite.guild.id]) : false

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {invite ? (
          <>
            {guildIconUrl(invite.guild.id, invite.guild.icon) ? (
              <span className={cx(styles.icon, styles.iconImage)} aria-hidden="true">
                <img src={guildIconUrl(invite.guild.id, invite.guild.icon)} alt="" />
              </span>
            ) : (
              <span className={styles.icon} style={{ background: guildColor(invite.guild.id) }} aria-hidden="true">{guildInitials(invite.guild.name)}</span>
            )}
            <p className={styles.lead}>{invite.inviter ? `${invite.inviter.global_name ?? invite.inviter.username} приглашает вас на сервер` : 'Вас приглашают на сервер'}</p>
            <h1 className={styles.name}>{invite.guild.name}</h1>
            <p className={styles.counts}>
              <span className={styles.dotOnline} /> {invite.approximate_presence_count} в сети
              <span className={styles.dotAll} /> {invite.approximate_member_count} {plural(invite.approximate_member_count, 'участник', 'участника', 'участников')}
            </p>
            {invite.guild.description ? <p className={styles.description}>{invite.guild.description}</p> : null}
            {error ? <p className={styles.error} role="alert">{error}</p> : null}
            {member ? (
              <button type="button" className={styles.primary} onClick={() => { openGuild(invite.guild.id, invite.channel.id); finish() }}>Вы уже здесь — открыть</button>
            ) : (
              <button type="button" className={styles.primary} disabled={busy} onClick={() => void accept()}>{busy ? 'Входим…' : 'Принять приглашение'}</button>
            )}
          </>
        ) : (
          <>
            <span className={styles.icon} aria-hidden="true"><Users size={32} /></span>
            <h1 className={styles.name}>{error ? 'Приглашение недействительно' : 'Ищем приглашение…'}</h1>
            <p className={styles.description}>{error ? 'Ссылка истекла, была отозвана или введена с ошибкой. Попросите новое приглашение.' : 'Секунду.'}</p>
          </>
        )}
        <button type="button" className={styles.later} onClick={finish}>{error ? 'Вернуться' : 'Не сейчас'}</button>
      </div>
    </div>
  )
}
